import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { Alert, Avatar, Box, Button, Chip, Divider, Paper, Skeleton, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useGetIdentity, useRedirect, useTranslate } from "react-admin";

import type { UserIdentity } from "../auth/lib";
import { AdminMfaSettingsSection } from "../components/AdminMfaSettingsSection";
import { OrganizationBillingSection } from "../components/OrganizationBillingSection";
import { UserType } from "../generated/graphql";
import { getAuthData } from "../lib/authStorage";
import { borderRadius, color, shadows, space, typography } from "../theme/designTokens";

type AccountDetailRowProps = {
  action?: ReactNode;
  icon: ReactNode;
  label: string;
  value: string;
};

const AccountDetailRow = ({ action, icon, label, value }: AccountDetailRowProps) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "flex-start", sm: "center" }}
    gap={space.sm}
    px={space.sm2}
    py={space.sm}
    minHeight={72}
  >
    <Box sx={{ color: color.primary, display: "flex" }}>{icon}</Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography color="text.secondary" fontSize={typography.fontSize.xs}>
        {label}
      </Typography>
      <Typography sx={{ overflowWrap: "anywhere" }}>{value}</Typography>
    </Box>
    {action}
  </Stack>
);

const getInitials = (identity: UserIdentity): string => {
  const names = [identity.firstName, identity.lastName].filter((name): name is string => !!name);
  const initialsSource = names.length > 0 ? names : identity.fullName.split(/\s+/).filter(Boolean);
  return initialsSource
    .slice(0, 2)
    .map(name => name.charAt(0).toUpperCase())
    .join("");
};

export const AccountSettingsPage = () => {
  const translate = useTranslate();
  const redirect = useRedirect();
  const { data, isPending } = useGetIdentity();
  const identity = data as UserIdentity | undefined;
  const companySession = getAuthData()?.method === "company";

  if (isPending) {
    return (
      <Stack gap={space.sm} maxWidth={760} data-testid="account-settings-loading">
        <Skeleton variant="rounded" height={126} />
        <Skeleton variant="rounded" height={300} />
      </Stack>
    );
  }

  if (!identity) return <Alert severity="error">{translate("account.loadError")}</Alert>;

  const fallback = translate("account.notAvailable");
  const role = translate(`account.roles.${identity.kind}`, { _: identity.kind });

  return (
    <Stack gap={space.sm2} maxWidth={760} data-testid="account-settings-page">
      <Paper
        variant="outlined"
        sx={{
          alignItems: "center",
          borderColor: color.panelBorder,
          borderRadius: borderRadius.panel,
          boxShadow: shadows.card,
          display: "flex",
          gap: space.sm,
          p: space.sm2,
        }}
      >
        <Avatar
          sx={{
            bgcolor: color.primaryTint,
            color: color.primary,
            fontSize: typography.fontSize.xl,
            height: 72,
            width: 72,
          }}
        >
          {getInitials(identity)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={typography.fontWeight.semibold}>
            {identity.fullName}
          </Typography>
          <Stack direction="row" alignItems="center" gap={space.xs} mt={space.xxs} flexWrap="wrap">
            <Chip label={role} size="small" />
            {identity.email && (
              <Typography color="text.secondary" fontSize={typography.fontSize.sm}>
                {identity.email}
              </Typography>
            )}
          </Stack>
        </Box>
      </Paper>

      <Box>
        <Typography variant="h6" mb={space.xs}>
          {translate("account.general")}
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            borderColor: color.panelBorder,
            borderRadius: borderRadius.panel,
            boxShadow: shadows.card,
            overflow: "hidden",
          }}
        >
          <AccountDetailRow
            icon={<ApartmentOutlinedIcon />}
            label={translate("account.organization")}
            value={identity.organizationName ?? fallback}
          />
          <Divider />
          <AccountDetailRow
            icon={<EmailOutlinedIcon />}
            label={translate("account.email")}
            value={identity.email ?? fallback}
          />
          <Divider />
          <AccountDetailRow icon={<ManageAccountsOutlinedIcon />} label={translate("account.role")} value={role} />
          <Divider />
          {companySession ? (
            <AccountDetailRow
              icon={<LockOutlinedIcon />}
              label={translate("account.signIn")}
              value={translate("account.companySignIn")}
            />
          ) : (
            <AccountDetailRow
              icon={<LockOutlinedIcon />}
              label={translate("account.password")}
              value="••••••••••••"
              action={
                <Button
                  variant="outlined"
                  startIcon={<AccountCircleOutlinedIcon />}
                  onClick={() => redirect("/change-password")}
                  data-testid="change-password-button"
                >
                  {translate("account.changePassword")}
                </Button>
              }
            />
          )}
        </Paper>
      </Box>
      {!companySession && [UserType.Manager, UserType.CustomerService, UserType.Analytics].includes(identity.kind) && (
        <AdminMfaSettingsSection />
      )}
      {identity.kind === UserType.Manager && <OrganizationBillingSection key={identity.id} />}
    </Stack>
  );
};
